import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { NodeGraph } from '@lakaran/nodes';
import {
  buildCardModel,
  CARD_HEADER,
  CARD_PAD,
  RAIL_WIDTH,
  ROW_HEIGHT,
  type CardModel,
  type CardRow,
} from './card-model';
/**
 * The node canvas, drawn with Pixi instead of DOM.
 *
 * One manager object owns the model of every card (position, size, rows) and two batched graphics —
 * card bodies and wires — so panning and zooming is a container transform, a dragged card is a
 * single rounded-rect repaint, and a 200-step graph is the same number of draw calls as a 5-step
 * one. Text is pooled per string and re-rastered only when the zoom crosses a step. Everything
 * interactive is reported back to the editor, which owns the store and the DOM field editor.
 */

export interface RowTarget {
  nodeId: string;
  row: CardRow;
  /** Screen rectangle of the row inside the panel, for placing a DOM editor over it. */
  screen: { x: number; y: number; width: number; height: number };
}

export interface NodesSceneCallbacks {
  onSelect(nodeId: string, additive: boolean): void;
  onClearSelection(): void;
  onDrag(dx: number, dy: number, done: boolean): void;
  onEditRow(target: RowTarget): void;
  onAction(nodeId: string, action: NonNullable<CardRow['action']>, rowId: string): void;
  /** Pointer left or entered a card; `null` when it is over empty canvas. */
  onHoverNode?(nodeId: string | null): void;
  /** The row under the pointer inside a card (`null` when there is none), for row-level highlights. */
  onHoverRow?(nodeId: string | null, rowId: string | null): void;
  onPalette(at: { x: number; y: number }): void;
  onDelete(): void;
}

export interface NodesScene {
  sync(graph: NodeGraph, selected: ReadonlySet<string>): void;
  /** True when the pointer event was handled by the canvas. */
  readonly ready: boolean;
  focus(): void;
  fit(): void;
  /** Pan the least amount that brings a card into view, keeping the zoom as it is. */
  reveal(x: number, y: number, width?: number, height?: number): void;
  destroy(): void;
  debugToScreen(x: number, y: number): { x: number; y: number };
}
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2.5;
const FONT = 'Inter, "Segoe UI", system-ui, sans-serif';

interface CardEntry {
  model: CardModel;
  signature: string;
  /** Was selected when last drawn, so selection can be repainted without a rebuild. */
  selected: boolean;
}

export async function createNodesScene(
  host: HTMLElement,
  callbacks: NodesSceneCallbacks,
): Promise<NodesScene> {
  const app = new Application();
  await app.init({
    preference: 'webgpu',
    background: 0xf4f5f6,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(2, window.devicePixelRatio || 1),
  });
  app.canvas.classList.add('nodes-gpu-canvas');
  app.canvas.tabIndex = -1;
  host.append(app.canvas);

  // The panel is laid out by flex, so its size at init is not its final size: follow it.
  const resize = (): void => {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    if (app.renderer.width === width && app.renderer.height === height) return;
    app.renderer.resize(width, height);
  };
  resize();
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
  observer?.observe(host);

  const world = new Container();
  app.stage.addChild(world);

  const bodies = new Graphics();
  const rows = new Graphics();
  const wires = new Graphics();
  const sockets = new Graphics();
  world.addChild(wires, bodies, rows, sockets);

  const texts = new Map<string, Text>();
  const shown = new Map<string, CardEntry>();
  let scale = 1;
  let panX = 0;
  let panY = 0;
  let zoomBucket = 1;

  const toWorld = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = app.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panX) / scale,
      y: (clientY - rect.top - panY) / scale,
    };
  };
  const toScreen = (x: number, y: number): { x: number; y: number } => ({
    x: x * scale + panX,
    y: y * scale + panY,
  });

  const camera = (): void => {
    world.position.set(panX, panY);
    world.scale.set(scale);
  };

  // ------------------------------------------------------------------- text

  const textKey = (kind: string, value: string): string => `${kind}|${value}`;

  const acquireText = (kind: 'title' | 'label' | 'value' | 'badge', value: string): Text => {
    const key = textKey(kind, value);
    // Mark the key as used before the cache lookup: the cleanup at the end of a draw destroys
    // every pooled text that this draw did not ask for.
    usedTextKeys.add(key);
    const cached = texts.get(key);
    if (cached) return cached;
    const base = kind === 'title' ? 13 : kind === 'badge' ? 12 : 11.5;
    const style = new TextStyle({
      fontFamily: FONT,
      fontSize: base,
      fill:
        kind === 'label'
          ? 0x55607a
          : kind === 'badge'
            ? 0xb45309
            : kind === 'title'
              ? 0x1f2937
              : 0x111827,
      fontWeight: kind === 'title' || kind === 'badge' ? '600' : '400',
    });
    const text = new Text({ text: value, style });
    text.resolution = 2;
    texts.set(key, text);
    world.addChild(text);
    return text;
  };

  const usedTextKeys = new Set<string>();

  const placeText = (
    kind: 'title' | 'label' | 'value' | 'badge',
    value: string,
    x: number,
    y: number,
    maxWidth: number,
    align: 'left' | 'right' = 'left',
  ): void => {
    if (!value) return;
    const text = acquireText(kind, value);
    text.visible = true;
    text.anchor.set(align === 'right' ? 1 : 0, 0);
    text.position.set(align === 'right' ? x + maxWidth : x, y);
    text.scale.set(1, 1);
    if (text.width > maxWidth) text.scale.set(Math.max(0.1, maxWidth / text.width), 1);
  };

  // ------------------------------------------------------------------ paint

  const draw = (): void => {
    usedTextKeys.clear();
    for (const text of texts.values()) text.visible = false;

    bodies.clear();
    rows.clear();
    sockets.clear();
    wires.clear();

    for (const entry of shown.values()) {
      const { model } = entry;
      const accent = model.isValue ? 0xd97706 : 0x2f6f4f;
      bodies
        .roundRect(model.x, model.y, model.width, model.height, 8)
        .fill({ color: model.isOutput ? 0xfef3e2 : 0xffffff, alpha: 0.98 })
        .stroke({
          color: entry.selected ? accent : 0xc9cfd8,
          width: entry.selected ? 1.8 : 1,
        });
      bodies
        .roundRect(model.x, model.y, model.width, CARD_HEADER, 8)
        .fill({ color: model.isOutput ? 0xf6d7ae : 0xeef0f3, alpha: 1 });

      placeText('title', model.title, model.x + 9, model.y + 6, model.width - 64);
      if (model.badge) {
        placeText('badge', model.badge, model.x + model.width - 12 - 58, model.y + 6, 58, 'right');
      }

      let y = model.y + CARD_HEADER + CARD_PAD;
      for (const row of model.rows) {
        if (row.kind === 'button') {
          rows
            .roundRect(model.x + 9, y + 2, model.width - 18, ROW_HEIGHT - 5, 4)
            .fill({ color: 0xeef4f0, alpha: 1 })
            .stroke({ color: 0xb9cdc0, width: 1 });
          placeText('label', row.text, model.x + 14, y + 4, model.width - 28);
          y += ROW_HEIGHT;
          continue;
        }
        const valueWidth = model.width * 0.46;
        placeText('label', row.label, model.x + 9, y + 4, model.width * 0.5 - 12);
        placeText(
          'value',
          row.text,
          model.x + model.width - 9 - valueWidth,
          y + 4,
          valueWidth,
          'right',
        );
        if (row.bound) {
          rows
            .circle(model.x + model.width - 4, y + ROW_HEIGHT / 2, 3.2)
            .fill({ color: 0xd97706 });
        }
        y += ROW_HEIGHT;
      }

      // Sockets: geometry in on the left, geometry out on the right, value out below the header.
      const midY = model.y + CARD_HEADER / 2;
      const declared = model.rows.some((row) => row.id.startsWith('in:'));
      if (!declared) sockets.circle(model.x, midY, 3.6).fill({ color: 0x94a3b8 });
      sockets.circle(model.x + model.width, midY, 3.6).fill({ color: model.isOutput ? 0x94a3b8 : 0x2f6f4f });
      if (model.isValue) {
        sockets
          .circle(model.x + model.width, model.y + model.height, 3.6)
          .fill({ color: 0xd97706 });
      }
      // A declared input (merge's A and B) gets its socket on the input edge, level with its row.
      for (let index = 0; index < model.rows.length; index++) {
        const row = model.rows[index]!;
        if (!row.id.startsWith('in:')) continue;
        sockets
          .circle(model.x, rowCentreY(model, index), 3.6)
          .fill({ color: row.text === '— none —' ? 0x94a3b8 : 0xc2410c });
      }
    }

    // Wires: one path per edge, all stroked together.
    for (const { from, to, slot } of wirePairs) {
      const a = shown.get(from);
      const b = shown.get(to);
      if (!a || !b) continue;
      const x1 = a.model.x + a.model.width;
      const y1 = a.model.y + CARD_HEADER / 2;
      const x2 = b.model.x;
      const slotRow = slot ? b.model.rows.findIndex((row) => row.id === `in:${slot}`) : -1;
      const y2 = slotRow >= 0 ? rowCentreY(b.model, slotRow) : b.model.y + CARD_HEADER / 2;
      const bend = Math.max(28, Math.abs(x2 - x1) * 0.45);
      wires
        .moveTo(x1, y1)
        .bezierCurveTo(x1 + bend, y1, x2 - bend, y2, x2, y2);
    }
    wires.stroke({ color: 0x8a6a4f, width: 1.4 / scale, alpha: 0.9 });

    for (const [key, text] of [...texts]) {
      if (usedTextKeys.has(key)) continue;
      texts.delete(key);
      text.destroy();
    }
  };

  // ------------------------------------------------------------------ model

  let wirePairs: Array<{ from: string; to: string; slot?: string }> = [];

  const sync = (graph: NodeGraph, selected: ReadonlySet<string>): void => {
    const seen = new Set<string>();
    let structureChanged = false;
    let bucket = Math.max(0.25, Math.round(scale * 4) / 4);
    if (bucket !== zoomBucket) {
      zoomBucket = bucket;
      structureChanged = true;
    }

    for (const node of graph.nodes) {
      seen.add(node.id);
      const entry = shown.get(node.id);
      const signature = cardSignatureOf(graph, node);
      const isSelected = selected.has(node.id);
      if (entry && entry.signature === signature) {
        // Cheap path: only position and selection can have changed.
        if (entry.model.x !== node.x || entry.model.y !== node.y) {
          entry.model.x = node.x;
          entry.model.y = node.y;
        }
        if (entry.selected !== isSelected) {
          entry.selected = isSelected;
          entry.model.selected = isSelected;
          structureChanged = true;
        }
        continue;
      }
      shown.set(node.id, {
        model: buildCardModel(graph, node, isSelected),
        signature,
        selected: isSelected,
      });
      structureChanged = true;
    }
    for (const id of [...shown.keys()]) {
      if (seen.has(id)) continue;
      shown.delete(id);
      structureChanged = true;
    }

    wirePairs = graph.edges
      .filter((edge) => shown.has(edge.from) && shown.has(edge.to))
      .map((edge) => ({ from: edge.from, to: edge.to, slot: edge.slot }));

    if (structureChanged) draw();
  };

  // ------------------------------------------------------------- interaction

  let drag: { lastX: number; lastY: number; moved: boolean } | null = null;
  let pan: { lastX: number; lastY: number } | null = null;
  let marquee: { start: { x: number; y: number }; current: { x: number; y: number } } | null = null;
  let hoverNodeId: string | null = null;
  let hoverRowId: string | null = null;
  let lastPointer: { x: number; y: number } | null = null;

  const rowAt = (
    worldX: number,
    worldY: number,
  ): { entry: CardEntry; row: CardRow; y: number } | null => {
    for (const entry of shown.values()) {
      const { model } = entry;
      if (worldX < model.x || worldX > model.x + model.width) continue;
      if (worldY < model.y || worldY > model.y + model.height) continue;
      let y = model.y + CARD_HEADER + CARD_PAD;
      for (const row of model.rows) {
        if (worldY >= y && worldY < y + ROW_HEIGHT) return { entry, row, y };
        y += ROW_HEIGHT;
      }
      return { entry, row: { id: 'head', kind: 'badge', label: '', text: '' }, y: model.y };
    }
    return null;
  };

  const capture = (pointerId: number, on: boolean): void => {
    try {
      if (on) app.canvas.setPointerCapture(pointerId);
      else app.canvas.releasePointerCapture(pointerId);
    } catch {
      // Synthetic pointers (and pointers released elsewhere) have no capture to give back.
    }
  };

  const onPointerDown = (event: PointerEvent): void => {
    app.canvas.focus({ preventScroll: true });
    const point = toWorld(event.clientX, event.clientY);
    if (event.button === 1 || (event.button === 0 && (event.altKey || event.metaKey))) {
      pan = { lastX: event.clientX, lastY: event.clientY };
      capture(event.pointerId, true);
      return;
    }
    if (event.button !== 0) return;
    const hit = rowAt(point.x, point.y);
    if (!hit) {
      marquee = { start: point, current: point };
      callbacks.onClearSelection();
      capture(event.pointerId, true);
      return;
    }
    const { entry, row } = hit;
    callbacks.onSelect(entry.model.id, event.shiftKey);
    if (row.kind === 'button' && row.action) {
      callbacks.onAction(entry.model.id, row.action, row.id);
      return;
    }
    if (row.id !== 'head') {
      const screenTopLeft = toScreen(entry.model.x + 6, hit.y);
      callbacks.onEditRow({
        nodeId: entry.model.id,
        row,
        screen: {
          x: screenTopLeft.x,
          y: screenTopLeft.y,
          width: (entry.model.width - 12) * scale,
          height: ROW_HEIGHT * scale,
        },
      });
      return;
    }
    drag = { lastX: event.clientX, lastY: event.clientY, moved: false };
    capture(event.pointerId, true);
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (pan) {
      panX += event.clientX - pan.lastX;
      panY += event.clientY - pan.lastY;
      pan.lastX = event.clientX;
      pan.lastY = event.clientY;
      camera();
      return;
    }
    if (drag) {
      const dx = (event.clientX - drag.lastX) / scale;
      const dy = (event.clientY - drag.lastY) / scale;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      drag.moved = true;
      callbacks.onDrag(dx, dy, false);
      return;
    }
    if (marquee) {
      marquee.current = toWorld(event.clientX, event.clientY);
      drawMarquee();
      return;
    }
    if (event.buttons === 0) {
      // Nothing is being dragged: report which card the pointer is over, so its shape can light up.
      lastPointer = { x: event.clientX, y: event.clientY };
      trackHover(event.clientX, event.clientY);
    }
  };

  /** Reports the card under a client point (`null` when it is empty canvas). */
  const trackHover = (clientX: number, clientY: number): void => {
    const point = toWorld(clientX, clientY);
    const hit = rowAt(point.x, point.y);
    const hovered = hit ? hit.entry.model.id : null;
    if (hovered !== hoverNodeId) {
      hoverNodeId = hovered;
      callbacks.onHoverNode?.(hovered);
    }
    // Row-level highlights (a trim span, a picked piece) need the row under the pointer too.
    const row = hit && hit.row.id !== 'head' ? hit.row.id : null;
    if (row !== hoverRowId) {
      hoverRowId = row;
      callbacks.onHoverRow?.(hovered, row);
    }
  };

  const drawMarquee = (): void => {
    rows.clear();
    if (!marquee) return;
    const x = Math.min(marquee.start.x, marquee.current.x);
    const y = Math.min(marquee.start.y, marquee.current.y);
    const width = Math.abs(marquee.current.x - marquee.start.x);
    const height = Math.abs(marquee.current.y - marquee.start.y);
    rows
      .rect(x, y, width, height)
      .fill({ color: 0x2f6f4f, alpha: 0.08 })
      .stroke({ color: 0x2f6f4f, width: 1 / scale });
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (pan) {
      pan = null;
      capture(event.pointerId, false);
      return;
    }
    if (drag) {
      const moved = drag.moved;
      drag = null;
      capture(event.pointerId, false);
      if (moved) callbacks.onDrag(0, 0, true);
      return;
    }
    if (marquee) {
      const x = Math.min(marquee.start.x, marquee.current.x);
      const y = Math.min(marquee.start.y, marquee.current.y);
      const width = Math.abs(marquee.current.x - marquee.start.x);
      const height = Math.abs(marquee.current.y - marquee.start.y);
      marquee = null;
      capture(event.pointerId, false);
      const touched = [...shown.values()].filter(
        (entry) =>
          entry.model.x < x + width &&
          entry.model.x + entry.model.width > x &&
          entry.model.y < y + height &&
          entry.model.y + entry.model.height > y,
      );
      if (touched.length > 0) {
        callbacks.onClearSelection();
        for (const entry of touched) callbacks.onSelect(entry.model.id, true);
      }
      draw();
      return;
    }
  };

  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const rect = app.canvas.getBoundingClientRect();
    const anchorX = event.clientX - rect.left;
    const anchorY = event.clientY - rect.top;
    const factor = Math.exp(-event.deltaY * 0.0015);
    const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale * factor));
    const ratio = next / scale;
    panX = anchorX - (anchorX - panX) * ratio;
    panY = anchorY - (anchorY - panY) * ratio;
    scale = next;
    camera();
    // The camera moved under the pointer, so the card under it may be a different one now.
    if (lastPointer) trackHover(lastPointer.x, lastPointer.y);
    const bucket = Math.max(0.25, Math.round(scale * 4) / 4);
    if (bucket !== zoomBucket) {
      zoomBucket = bucket;
      // Text is rasterised per zoom step, so the pool has to be rebuilt when the step changes.
      for (const text of texts.values()) text.destroy();
      texts.clear();
    }
  };

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      callbacks.onDelete();
      return;
    }
    if (event.key === 'Escape') callbacks.onClearSelection();
  };

  app.canvas.addEventListener('pointerdown', onPointerDown);
  app.canvas.addEventListener('pointermove', onPointerMove);
  app.canvas.addEventListener('pointerup', onPointerUp);
  app.canvas.addEventListener('pointercancel', onPointerUp);
  // Leaving the canvas must forget the hover, or the selection's glow would never get a turn.
  app.canvas.addEventListener('pointerleave', () => {
    lastPointer = null;
    if (hoverNodeId === null && hoverRowId === null) return;
    const hadRow = hoverRowId !== null;
    hoverNodeId = null;
    hoverRowId = null;
    callbacks.onHoverNode?.(null);
    if (hadRow) callbacks.onHoverRow?.(null, null);
  });
  app.canvas.addEventListener('wheel', onWheel, { passive: false });
  app.canvas.addEventListener('keydown', onKey);
  app.canvas.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    const rect = app.canvas.getBoundingClientRect();
    callbacks.onPalette({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  });

  camera();

  return {
    sync,
    get ready() {
      return true;
    },
    focus() {
      app.canvas.focus({ preventScroll: true });
    },
    /** Test/debug helper: where a world point lands inside the panel. */
    debugToScreen(x: number, y: number) {
      return toScreen(x, y);
    },
    fit() {
      // A zero-sized host means the panel has not been laid out yet: framing now would clamp the
      // zoom to the minimum, so wait for a real size instead.
      if ((host.clientWidth || 0) < 2 || (host.clientHeight || 0) < 2) return;
      const cards = [...shown.values()].map((entry) => entry.model);
      if (cards.length === 0) return;
      const minX = Math.min(...cards.map((card) => card.x)) - 40;
      const minY = Math.min(...cards.map((card) => card.y)) - 40;
      const maxX = Math.max(...cards.map((card) => card.x + card.width)) + 40;
      const maxY = Math.max(...cards.map((card) => card.y + card.height)) + 40;
      // The insert rail covers the left edge, so frame inside the strip that is actually free.
      const rail = RAIL_WIDTH + 12;
      const width = Math.max(1, (host.clientWidth || 1) - rail);
      const height = host.clientHeight || 1;
      scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(width / (maxX - minX), height / (maxY - minY))));
      panX = rail + (width - (maxX - minX) * scale) / 2 - minX * scale;
      panY = (height - (maxY - minY) * scale) / 2 - minY * scale;
      camera();
    },
    reveal(x: number, y: number, width = 230, height = 180) {
      const rail = RAIL_WIDTH + 12;
      const viewWidth = host.clientWidth || 1;
      const viewHeight = host.clientHeight || 1;
      const margin = 10;
      const left = x * scale + panX;
      const top = y * scale + panY;
      const cardWidth = width * scale;
      const cardHeight = height * scale;
      if (cardWidth > viewWidth - rail - margin * 2) panX = rail + margin - x * scale;
      else if (left < rail + margin) panX += rail + margin - left;
      else if (left + cardWidth > viewWidth - margin) panX -= left + cardWidth - (viewWidth - margin);
      if (cardHeight > viewHeight - margin * 2) panY = margin - y * scale;
      else if (top < margin) panY += margin - top;
      else if (top + cardHeight > viewHeight - margin) panY -= top + cardHeight - (viewHeight - margin);
      camera();
    },
    destroy() {
      observer?.disconnect();
      app.destroy(true, { children: true });
    },
  };
}

function cardSignatureOf(graph: NodeGraph, node: NodeGraph['nodes'][number]): string {
  // Reuse the model builder's signature so both editors invalidate on the same events.
  return buildCardModel(graph, node, false).signature;
}

/** Vertical centre of a card row, in world units — where a socket or a wire end sits. */
function rowCentreY(model: CardModel, index: number): number {
  return model.y + CARD_HEADER + CARD_PAD + index * ROW_HEIGHT + ROW_HEIGHT / 2;
}


